import React from "react"
import { useCheckout } from "../../../features/checkout/state/hooks"
import StickyContainer from "../../../ui/molecules/StickyContainer"
import BasketItemsList from "../../../features/checkout/components/basket/BasketContent/BasketItemsList"
import PurchaseCta from "../../../features/checkout/components/basket/BasketContent/PurchaseCta"
import FreeShippingAnimation from "../../../features/checkout/components/basket/BasketContent/FreeShippingAnimation"
import BasketWrapper from "../../../features/checkout/components/basket/BasketWrapper"
import MobileOnly from "../../../root/punks/mui/visibility/MobileOnly"
import {
  CustomContainer,
  CustomRow,
  CustomCol,
  CustomBox,
} from "../../../ui/spacings"
import { DefaultLayout } from "../../../layouts/Default/DefaultLayout"
import CustomButton from "../../../ui/atoms/Button"
import { BackArrowIcon } from "../../../ui/icons"
import SamplesBox from "../../../features/checkout/components/samples/SamplesBox"
import Typography from "../../../ui/atoms/Typography"
import Stroke from "../../../ui/atoms/Stroke"
import { useRouter } from "next/router"
import { useLabels } from "../../../features/contents/hooks"
import { useBasketSamples } from "../../../features/checkout/components/hooks/useBasketSamples"
import { BasketShippingHeader } from "../../../features/checkout/components/basket/BasketContent/BasketShippingHeader"
import { useProductRecommendations } from "../../../features/checkout/components/hooks/useProductRecommendations"
import { useBasketProductActions } from "../../../features/checkout/components/hooks/useBasketProductActions"
import { useAppNavigation } from "../../../features/navigation/hooks/useIsFirstPage"
import { useCheckoutNavigation } from "../../../features/checkout/components/hooks/useCheckoutNavigation"

const BasketPageContainer = () => {
  const router = useRouter()
  const { basket } = useCheckout()

  const { getLabel } = useLabels()
  const samples = useBasketSamples()

  const selectedSamples = basket?.samples.samples.map((x) => x.id) ?? []

  const {
    toggleSample,
    handleBasketItemRemove,
    handleBasketItemGiftSelect,
    handleBasketItemMoveToWishlist,
    handleBasketItemQuantityChange,
    handleBasketItemVariantChange,
  } = useBasketProductActions()

  const { isFirstPage } = useAppNavigation()

  const {
    getRecommendations,
    loadingRecommendations,
  } = useProductRecommendations()

  const {
    navigateToCheckout,
    navigateToRecommendations,
  } = useCheckoutNavigation()

  const handleNext = async () => {
    if (!basket) {
      throw new Error("Basket is not defined")
    }
    const recommendations = await getRecommendations(basket)
    if (recommendations.length > 0) {
      navigateToRecommendations()
      return
    }

    navigateToCheckout()
  }

  return (
    <DefaultLayout hideFooterMobile contentFlex disableScrollTop>
      <CustomContainer xlNoGutters className="flex-1">
        <BasketWrapper>
          {basket && !basket.empty && (
            <CustomRow spacing>
              <CustomCol xs={12}>
                <div className="mb-2">
                  <div className="bg-disabled">
                    <BasketShippingHeader
                      basket={basket}
                      className="px-2 py-3"
                    />
                    <FreeShippingAnimation basket={basket} />
                  </div>
                  <div className="px-6 mt-4 mb-2">
                    <SamplesBox
                      samples={samples}
                      selectedSamples={selectedSamples}
                      toggleSample={(sampleId) =>
                        toggleSample(sampleId, samples, basket)
                      }
                    />
                  </div>
                  <Stroke thickness={4} />
                  <div className="mx-2 mt-4">
                    <Typography className="pl-4" variant="subtitle1">
                      {getLabel("basket", "basket-title")}
                    </Typography>
                    <BasketItemsList
                      basket={basket}
                      onProductRemove={handleBasketItemRemove}
                      onProductMoveToWishlist={handleBasketItemMoveToWishlist}
                      onProductQuantityChange={handleBasketItemQuantityChange}
                      onProductVariantChange={handleBasketItemVariantChange}
                      onProductGiftToggle={handleBasketItemGiftSelect}
                    />
                  </div>
                </div>
              </CustomCol>
            </CustomRow>
          )}
        </BasketWrapper>
      </CustomContainer>
      {basket && (
        <StickyContainer bottom={0}>
          <MobileOnly lg>
            <CustomBox background="white" px={1} py={2}>
              <CustomButton
                variant="text"
                invariantCase
                fullWidth
                onClick={() => (isFirstPage ? router.push("/") : router.back())}
              >
                <BackArrowIcon className="mt-1 mr-2" />
                <span> {getLabel("basket", "basket-back")}</span>
              </CustomButton>
              <PurchaseCta
                basket={basket}
                loading={loadingRecommendations}
                onNext={() => handleNext()}
              />
            </CustomBox>
          </MobileOnly>
        </StickyContainer>
      )}
    </DefaultLayout>
  )
}

export default BasketPageContainer
